import type React from "react";
import { useNavigate } from "react-router";

import { Glasses02Icon } from "@/components/Icons/Glasses02Icon";
import { MessageHeartSquareIcon } from "@/components/Icons/MessageHeartSquareIcon";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import { Redirect } from "@/components/Routing/Redirect";
import { Button } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useSlug } from "@/hooks/useSlug";
import { OnboardingFlowCard } from "@/modules/onboarding-flow/components/OnboardingFlowCard";
import { OnboardingFlowActions, OnboardingFlowRoot } from "@/modules/onboarding-flow/components/OnboardingFlowLayout";
import { OnboardingTopBar } from "@/modules/onboarding-flow/components/OnboardingTopBar";
import { userMutations } from "@/queries/users/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function OnboardingCodeOfConductPage(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t("page.onboarding.code-of-conduct.title"));
  const sessionUser = useSessionUser();
  const navigate = useNavigate();
  const slug = useSlug();

  const markCodeOfConductAsAgreedMutation = userMutations.useMarkCodeOfConductAsAgreed();

  const handleAgree = async () => {
    await markCodeOfConductAsAgreedMutation.mutateAsync();
    void navigate(routes.onboardingFlow.userProfile({ slug }));
  };

  if (sessionUser.codeOfConductAgreedAt) {
    return <Redirect to={({ slug: currSlug }) => routes.onboardingFlow.userProfile({ slug: currSlug })} />;
  }

  return (
    <OnboardingFlowRoot>
      <OnboardingTopBar />
      <OnboardingFlowCard>
        <Stack gap="sm">
          <MessageHeartSquareIcon size={40} />
          <h3 className="text-heading-3">{t("page.onboarding.code-of-conduct.title")}</h3>
        </Stack>
        <MultiLineContent>{t("page.onboarding.code-of-conduct.body")}</MultiLineContent>
        <Button
          data-testid="coc-link"
          icon={<Glasses02Icon />}
          variant="ghostPrimary"
          type="link"
          href={sessionUser.codeOfConduct.url}
          target="_blank"
          isExternal
        >
          {t("page.onboarding.code-of-conduct.link.read-code-of-conduct")}
        </Button>
        <OnboardingFlowActions>
          <Stack gap="sm">
            <Button
              fullWidth
              onClick={handleAgree}
              data-testid="coc-agree-btn"
              isLoading={markCodeOfConductAsAgreedMutation.isPending}
            >
              {t("page.onboarding.code-of-conduct.btn.agree")}
            </Button>
            <Button
              type="link"
              variant="soft"
              href={routes.onboardingFlow.slides({
                slug,
              })}
              fullWidth
              disabled={markCodeOfConductAsAgreedMutation.isPending}
            >
              {t("common.action.back")}
            </Button>
          </Stack>
        </OnboardingFlowActions>
      </OnboardingFlowCard>
    </OnboardingFlowRoot>
  );
}
